<template>
  <a-col class="security-protocol">
    <!-- SMS -->
    <BasicButton
      @click="onSendViaSms"
      :disabled="
        isSmsLoading || isLoading || startCountingSms || $attrs.disabled
      "
      :loading="isSmsLoading || isLoading"
      v-if="isSmsAllowed && !hideSmsOption"
      class="w-100 mt-10 security-protocol__basic-button"
    >
      <!-- Use the coutdown value form vuex store to avoid spamming of otp send via sms -->
      <StatisticCountdown
        v-if="startCountingSms"
        :prefix="t('Resend code via SMS in')"
        :countdownValueInMs="sendOtpViaSmsLastSentInMs"
        @finish="onResendSmsFinish"
      />
      <template v-else>{{ t("Receive code via SMS") }} </template>
    </BasicButton>

    <!-- Email -->
    <BasicButton
      @click="onSendViaEmail"
      :disabled="
        isEmailLoading || isLoading || startCountingEmail || $attrs.disabled
      "
      :loading="isEmailLoading || isLoading"
      v-if="isEmailAllowed && !hideEmailOption"
      class="w-100 mt-10 security-protocol__basic-button"
    >
      <StatisticCountdown
        v-if="startCountingEmail"
        :prefix="t('Resend code via Email in')"
        @finish="onResendEmailFinish"
      />
      <template v-else>
        {{ t("Receive code via Email") }}
      </template>
    </BasicButton>

    <!-- Backup Codes -->
    <div v-if="isBackupCodesAllowed && !hideBackupCodeOption">
      <!-- FIXME: Backup code instruction is subjected for change -->
      <div class="ant-row ant-row-center">
        <a-divider />

        {{
          t(
            "You can also try a backup code, if you have one. Check your available backup codes via the Settings page."
          )
        }}
      </div>
    </div>
  </a-col>
</template>

<style lang="scss">
.security-protocol {
  /** Margin in between loading 
  * indicator and Resend OTP Label */
  .ant-statistic {
    margin-left: 10px;
  }
  /** For Loading indicator and Resend 
  * OTP Label to be inline */
  .basic-button {
    display: flex;
    justify-content: center;
    color: $text-color;
    border: 1px solid $border-color-base;
    border-radius: $border-radius-base;
  }

  // Button Styles
  .ant-btn.basic-button {
    height: 40px;
    padding: 10px;
    font-weight: $btn-font-weight;
  }

  @media screen and (max-width: $screen-sm) {
    // Button Styles
    .ant-btn.basic-button {
      min-height: 40px;
      height: auto;
      padding: $padding-xss;
      align-items: center;
    }
  }
}
</style>

<script lang="ts">
import { defineComponent, ref, PropType, computed, watch } from "vue";
import {
  useUserSecurityProtocols,
  UserSecurityProtocols,
} from "@/shared/composables/useUserSecurityProtocols";
import { useI18n } from "vue-i18n";
import BasicButton from "@/shared/components/buttons/BasicButton.vue";
import { useOtpSendViaEmail } from "@/shared/composables/api/useOtpSendViaEmail";
import { useOtpSendViaSms } from "@/shared/composables/api/useOtpSendViaSms";
import StatisticCountdown from "@/shared/components/forms/StatisticCountdown.vue";
import { loginSimpleStore } from "@/shared/store/simpleStore/loginData";
import moment from "moment";
import { useStore } from "vuex";
import { millisecondsLeftToSendOtp } from "@/shared/utils/time";
import useLogging from "@/shared/composables/useLogging";

export default defineComponent({
  emits: ["on-sms-send-success"],
  setup(props, { emit }) {
    const t = useI18n().t;
    const store = useStore();
    const startCountingSms = ref(false);
    const startCountingEmail = ref(false);

    // Fetch the last OTP sent from store for send otp via sms
    const sendOtpViaSmsLastOtpSent = computed(
      () => new Date(store.state.userInfo.sendOtpViaSmsLastSent)
    );
    // Fetch last opt sent in seconds
    const sendOtpViaSmsLastSentInMs = computed(() => {
      return millisecondsLeftToSendOtp(
        moment(),
        moment(sendOtpViaSmsLastOtpSent.value)
      );
    });

    // watch the last sent otp via sms
    watch(
      [sendOtpViaSmsLastSentInMs],
      ([lastSentOtpInMs]) => {
        // if the timer is still not done counting
        if (lastSentOtpInMs > 0) {
          // set start counting sms to true to toggle the display of countdown timer
          startCountingSms.value = true;
        }
      },
      {
        immediate: true,
      }
    );

    const { allowOtpSms, allowOtpEmail, allowBackupCodes } =
      useUserSecurityProtocols();

    const {
      sendOtpViaEmailLoading,
      sendOtpViaEmail,
      sendOtpViaEmailNonAuthLoading,
      sendOtpViaEmailNonAuth,
    } = useOtpSendViaEmail();

    const {
      sendOtpViaSms,
      sendOtpViaSmsLoading,
      sendOtpViaSmsNonAuth,
      sendOtpViaSmsNonAuthLoading,
    } = useOtpSendViaSms();

    // Logging API
    const { submitErrorToApi } = useLogging();

    const onSendViaSms = async () => {
      try {
        if (props.isNonAuthenticated) {
          await sendOtpViaSmsNonAuth({
            username: loginSimpleStore.username,
            password: loginSimpleStore.password,
          });
        } else {
          await sendOtpViaSms();
        }

        // emit to parent component
        emit("on-sms-send-success");

        // start counting
        startCountingSms.value = true;
      } catch (error) {
        submitErrorToApi("Error on sending OTP via SMS", {
          error,
        });
      }
    };

    const onSendViaEmail = async () => {
      if (props.isNonAuthenticated) {
        await sendOtpViaEmailNonAuth({
          username: loginSimpleStore.username,
          password: loginSimpleStore.password,
        });
      } else {
        await sendOtpViaEmail();
      }
      startCountingEmail.value = true;
    };

    const isSmsAllowed = computed(
      () => props.userSecurityProtocols?.allowOtpSms || allowOtpSms
    );

    const isEmailAllowed = computed(
      () => props.userSecurityProtocols?.allowOtpEmail || allowOtpEmail
    );

    const isBackupCodesAllowed = computed(
      () => props.userSecurityProtocols?.allowBackupCodes || allowBackupCodes
    );

    return {
      t,
      isSmsAllowed,
      isEmailAllowed,
      isBackupCodesAllowed,
      isSmsLoading: computed(
        () => sendOtpViaSmsLoading.value || sendOtpViaSmsNonAuthLoading.value
      ),
      isEmailLoading: computed(
        () =>
          sendOtpViaEmailLoading.value || sendOtpViaEmailNonAuthLoading.value
      ),
      onSendViaSms,
      onSendViaEmail,
      startCountingSms,
      startCountingEmail,
      // Last sent for sms in seconds
      sendOtpViaSmsLastSentInMs,
      /**
       * Method is called automatically when the
       * countdown for OTP is finish or done
       */
      onResendSmsFinish: () => {
        startCountingSms.value = false;
      },
      onResendEmailFinish: () => {
        startCountingEmail.value = false;
      },
    };
  },
  components: {
    BasicButton,
    StatisticCountdown,
  },
  props: {
    /** optional props for instances where stored User data is not available
     * see usage in LoginOTP.vue
     */
    userSecurityProtocols: {
      type: Object as PropType<UserSecurityProtocols>,
    },
    /** toggle hiding of a specific security protocol option
     * for instances where the related option is subjected for change
     * to avoid complication
     * see sample implementation in GeneralSettingsLayout.vue
     */
    hideSmsOption: Boolean,
    hideEmailOption: Boolean,
    hideBackupCodeOption: Boolean,
    isLoading: {
      type: Boolean,
      default: false,
    },
    // trigger point to know whether to call non authenticated mutations or not
    isNonAuthenticated: Boolean,
  },
});
</script>
